// /bookings — Customer Booking Management (G3.7)
// List view with festival + location + status + date-range filters, KPI cards,
// responsive table/card results. Wired to GET /festivals/:id/bookings.
// RESTAURANT vertical · MANAGER+ · ENABLE_FESTIVE_MODULE.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Select,
  Input,
  DataTable,
  Skeleton,
  DatePicker,
  EmptyState,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import {
  useBookings,
  useBookingStats,
  useAllBookings,
  useAllBookingsStats,
  useFestivalsLookup,
  BookingStatusBadge,
  BookingMobileCard,
  STATUS_FILTER_OPTIONS,
  SLOT_OPTIONS,
  formatDeliveryTime,
  PAGE_SIZE,
  formatBookingDate,
  depositPercent,
} from '@/features/bookings';
import type {
  BookingListItem,
  BookingStatus,
  DeliverySlot,
} from '@/features/bookings';
import { NewBookingModal } from './NewBookingModal';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { locationService } from '@/services/location.service';
import { useQuery } from '@tanstack/react-query';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatPaise, formatPaiseCompact } from '@/utils/currency';
import { cn, GLASS, GLASS_FILTER } from '@/constants/design-tokens';
import {
  CalendarIcon,
  GridIcon,
  ListIcon,
  UsersIcon,
  CurrencyIcon,
  ClockIcon,
  AlertIcon,
  SearchIcon,
} from './icons';

interface KpiConfig {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  gradient: string;
  iconBg: string;
  tintBg: string;
  darkTintBg: string;
}

function KpiCard({ title, value, subtitle, icon, gradient, iconBg, tintBg, darkTintBg }: KpiConfig) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-5 animate-slide-up',
        tintBg,
        darkTintBg,
      )}
    >
      <div className={cn('absolute -top-8 -right-6 w-24 h-24 rounded-full opacity-20', gradient)} />
      <div className="relative flex items-center gap-3 mb-3">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm', iconBg)}>
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</span>
      </div>
      <div className="relative">
        <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums font-mono">{value}</p>
        {subtitle && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function BookingsListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;

  const page = Number(searchParams.get('page') ?? '1');
  const festivalParam = searchParams.get('festival') ?? '';
  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('q') ?? '';
  const slot = (searchParams.get('slot') ?? '') as DeliverySlot | '';
  const locationParam = searchParams.get('location') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';

  const { data: festivalsList, isLoading: festivalsLoading } = useFestivalsLookup();
  const festivals = festivalsList ?? [];

  // Auto-select nearest-upcoming active festival when none chosen.
  const defaultFestivalId = useMemo(() => {
    if (festivalParam) return festivalParam;
    if (!festivals.length) return '';
    const active = festivals.find((f) => f.status === 'ACTIVE');
    if (active) return active.id;
    const upcoming = festivals
      .filter((f) => f.status === 'UPCOMING')
      .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())[0];
    return upcoming?.id ?? festivals[0]?.id ?? '';
  }, [festivalParam, festivals]);

  const ALL_FESTIVALS = 'all';
  const isAllFestivals = festivalParam === ALL_FESTIVALS;
  const effectiveFestivalId = isAllFestivals ? '' : (festivalParam || defaultFestivalId);
  const activeFestival = festivals.find((f) => f.id === effectiveFestivalId);

  // Locations dropdown — admins may want to filter cross-location.
  const { data: locationsData } = useQuery({
    queryKey: ['bookings-locations'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locations = locationsData?.data ?? [];
  const effectiveLocationId = locationParam || selectedLocationId || '';

  const [searchInput, setSearchInput] = useState(search);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [newBookingOpen, setNewBookingOpen] = useState(false);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set('q', value);
          else next.delete('q');
          next.set('page', '1');
          return next;
        });
      }, 300);
    },
    [setSearchParams],
  );

  // Trigger search immediately (flush the debounce) — used by the icon button & Enter key.
  const searchNow = useCallback(() => {
    clearTimeout(searchTimerRef.current);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (searchInput) next.set('q', searchInput);
      else next.delete('q');
      next.set('page', '1');
      return next;
    });
  }, [searchInput, setSearchParams]);

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.set('page', '1');
      return next;
    });
  }

  function setPage(p: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(p));
      return next;
    });
  }

  function setSingleDate(d: Date | undefined) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (d) next.set('from', d.toLocaleDateString('en-CA'));
      else next.delete('from');
      next.delete('to');
      next.set('page', '1');
      return next;
    });
  }

  const single = useBookings(
    !isAllFestivals && effectiveFestivalId
      ? {
          festival_id: effectiveFestivalId,
          page,
          limit: PAGE_SIZE,
          status: (status as BookingStatus) || undefined,
          delivery_date: from && !to ? from : undefined,
          location_id: effectiveLocationId || undefined,
          search: search || undefined,
        }
      : null,
  );
  // Festival-wide KPI aggregate for single-festival mode — the list query only
  // returns one page, so the KPIs below must come from this server-side total
  // (GitHub issue #289). "All festivals" mode holds the full merged set client
  // side (filteredBookings), so it derives KPIs from that instead.
  const statsQuery = useBookingStats(
    !isAllFestivals && effectiveFestivalId
      ? {
          festival_id: effectiveFestivalId,
          status: (status as BookingStatus) || undefined,
          delivery_date: from && !to ? from : undefined,
          location_id: effectiveLocationId || undefined,
          search: search || undefined,
        }
      : null,
  );
  // "All festivals" mode — a single server-side paginated, filtered query plus a
  // scope-wide stats aggregate (GitHub issue #296), mirroring single-festival
  // mode instead of fanning out one request per festival and merging on the
  // client. Slot + delivery-date-range stay client-side post-filters (as in
  // single-festival mode — the backend only supports a single delivery_date).
  const allFilters = isAllFestivals
    ? {
        status: (status as BookingStatus) || undefined,
        delivery_date: from && !to ? from : undefined,
        location_id: effectiveLocationId || undefined,
        search: search || undefined,
      }
    : null;
  const allFestivalsQuery = useAllBookings(allFilters ? { ...allFilters, page, limit: PAGE_SIZE } : null);
  const allStatsQuery = useAllBookingsStats(allFilters);

  const data = isAllFestivals ? allFestivalsQuery.data : single.data;
  const isLoading = isAllFestivals ? allFestivalsQuery.isLoading : single.isLoading;
  const error = isAllFestivals ? allFestivalsQuery.error : single.error;

  useEffect(() => {
    if (error) toast({ title: (error as Error).message, variant: 'error' });
  }, [error, toast]);

  // Client-side narrowing for slot + delivery-date range (backend only supports a
  // single delivery_date param; we post-filter so the range picker feels native).
  // Applies to both modes; the list itself is server-paginated (GitHub issue #296).
  const filteredBookings: BookingListItem[] = useMemo(() => {
    const rows = data?.data ?? [];
    return rows.filter((b) => {
      if (slot && b.delivery_slot !== slot) return false;
      if (from && to) {
        const d = new Date(b.delivery_date);
        if (d < new Date(from) || d > new Date(to)) return false;
      }
      return true;
    });
  }, [data, slot, from, to]);

  // Both modes are server-paginated now — render the current page as-is.
  const bookings: BookingListItem[] = filteredBookings;
  const total = data?.total ?? 0;

  // KPI derivations — scope-wide, NOT page-scoped (GitHub issues #289, #296).
  // Both modes read a server-side aggregate: single-festival from the
  // /festivals/:id/bookings/stats endpoint, "All festivals" from /bookings/stats.
  const { upcomingCount, balanceSum, cancelledCount, cancellationRate } = useMemo(() => {
    const s = isAllFestivals ? allStatsQuery.data : statsQuery.data;
    const cancelled = s?.cancelled_count ?? 0;
    const rate = s && s.total_count ? Math.round((cancelled / s.total_count) * 100) : 0;
    return {
      upcomingCount: s?.upcoming_count ?? 0,
      balanceSum: s?.balance_sum ?? 0,
      cancelledCount: cancelled,
      cancellationRate: rate,
    };
  }, [isAllFestivals, allStatsQuery.data, statsQuery.data]);

  // ── Keyboard shortcut: `/` focuses search ───────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const columns: ColumnDef<BookingListItem>[] = [
    {
      key: 'booking_number',
      header: 'Booking #',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/bookings/${row.id}`)}
          className="text-sm text-brand-600 dark:text-brand-400 hover:underline font-mono tabular-nums font-medium text-left"
        >
          {row.booking_number}
        </button>
      ),
    },
    {
      key: 'customer_name',
      header: 'Customer',
      render: (_v, row) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate">
            {row.customer_name}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">
            {row.customer_phone}
          </p>
        </div>
      ),
    },
    {
      key: 'delivery_date',
      header: 'Delivery',
      render: (_v, row) => (
        <div className="min-w-0">
          <p className="text-sm text-neutral-700 dark:text-neutral-200 font-mono tabular-nums">
            {formatBookingDate(row.delivery_date)}
          </p>
          {row.delivery_slot && (
            <p className="text-xs text-violet-600 dark:text-violet-300">
              {formatDeliveryTime(row.delivery_slot)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'item_count',
      header: 'Items',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300 font-mono tabular-nums">
          {row.item_count}
        </span>
      ),
    },
    {
      key: 'total_amount',
      header: 'Total',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 font-mono tabular-nums">
          {formatPaise(row.total_amount, currency)}
        </span>
      ),
    },
    {
      key: 'deposit',
      header: 'Paid',
      numeric: true,
      render: (_v, row) => {
        const pct = depositPercent(row.advance_amount, row.total_amount);
        return (
          <div className="flex items-center justify-end gap-2 min-w-[120px]">
            <div className="h-1.5 w-16 rounded-full bg-neutral-200 dark:bg-neutral-700 overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  pct >= 100
                    ? 'bg-gradient-to-r from-success-500 to-teal-500'
                    : pct > 0
                      ? 'bg-gradient-to-r from-brand-500 to-indigo-500'
                      : 'bg-warning-500',
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-mono tabular-nums text-neutral-600 dark:text-neutral-300">
              {pct}%
            </span>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => <BookingStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/bookings/${row.id}`)}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
        >
          View →
        </button>
      ),
    },
  ];

  // ── Loading state ─────────────────────────────────────────────────────────
  if ((isLoading && !data) || festivalsLoading) {
    return (
      <>
        <Skeleton variant="rect" height={120} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={64} className="rounded-xl mb-4" />
        <Skeleton variant="rect" height={420} className="rounded-xl" />
      </>
    );
  }

  // ── No festivals at all — hard empty ───────────────────────────────────────
  if (!festivalsLoading && festivals.length === 0) {
    return (
      <>
        <header className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 p-6 text-white shadow-[0_20px_60px_-24px_rgba(79,70,229,0.55)] mb-6 animate-fade-in">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-black/20 blur-3xl" />
          <div className="relative">
            <h1 className="text-2xl font-bold text-white">Customer Bookings</h1>
            <p className="text-brand-100 mt-1">Manage festive pre-orders, deposits, and delivery reminders</p>
          </div>
        </header>
        <EmptyState
          variant="page"
          icon={<CalendarIcon className="h-full w-full" />}
          title="No festivals configured"
          description="Bookings are scoped to festivals. Create a festive event to start collecting customer pre-orders."
          action={
            isManager
              ? { label: 'Go to Festive Events', onClick: () => navigate('/festive-events') }
              : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <header className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 p-6 text-white shadow-[0_20px_60px_-24px_rgba(79,70,229,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-black/20 blur-3xl" />
        <div className="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-white">Customer Bookings</h1>
            <p className="text-brand-100 mt-1 text-sm">
              {isAllFestivals
                ? 'Festive pre-orders across all events'
                : activeFestival
                  ? `Festive pre-orders for ${activeFestival.name}`
                  : 'Select a festival to review pre-orders'}
            </p>
          </div>
          <div className="flex items-center gap-2 self-start lg:self-auto">
            {isManager && effectiveFestivalId && (
              <button
                type="button"
                onClick={() => setNewBookingOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-brand-700 hover:bg-brand-50 text-sm font-semibold shadow-sm transition-colors"
              >
                <span className="text-base leading-none">+</span>
                New Booking
              </button>
            )}
            <Link
              to={`/bookings/calendar${effectiveFestivalId ? `?festival=${effectiveFestivalId}` : ''}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium backdrop-blur-sm transition-colors"
            >
              <GridIcon className="h-4 w-4" />
              Calendar view
            </Link>
          </div>
        </div>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <KpiCard
          title="Total bookings"
          value={total.toLocaleString('en-IN')}
          subtitle={isAllFestivals ? 'All events' : activeFestival?.name}
          gradient="bg-gradient-to-br from-brand-500 to-indigo-600"
          iconBg="bg-gradient-to-br from-brand-500 to-indigo-600"
          tintBg="bg-brand-50"
          darkTintBg="dark:bg-brand-900/20"
          icon={<UsersIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="Upcoming ≤7d"
          value={upcomingCount.toLocaleString('en-IN')}
          subtitle="Awaiting delivery"
          gradient="bg-gradient-to-br from-violet-500 to-pink-500"
          iconBg="bg-gradient-to-br from-violet-500 to-pink-500"
          tintBg="bg-violet-50"
          darkTintBg="dark:bg-violet-900/20"
          icon={<ClockIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="Balance due"
          value={formatPaiseCompact(balanceSum, currency)}
          subtitle={isAllFestivals ? 'Across all events' : 'Across all bookings'}
          gradient="bg-gradient-to-br from-warning-500 to-warning-700"
          iconBg="bg-gradient-to-br from-warning-500 to-warning-600"
          tintBg="bg-warning-50"
          darkTintBg="dark:bg-warning-900/20"
          icon={<CurrencyIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="Cancellation rate"
          value={`${cancellationRate}%`}
          subtitle={`${cancelledCount} cancelled`}
          gradient="bg-gradient-to-br from-danger-500 to-rose-600"
          iconBg="bg-gradient-to-br from-danger-500 to-rose-600"
          tintBg="bg-rose-50"
          darkTintBg="dark:bg-rose-900/20"
          icon={<AlertIcon className="h-5 w-5" />}
        />
      </div>

      {/* Filters */}
      <div className={cn(GLASS_FILTER, 'p-3 mb-4 flex flex-wrap items-end gap-3')}>
        <div className="w-56 min-w-[200px]">
          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            Festival
          </label>
          <Select
            options={[
              { value: ALL_FESTIVALS, label: 'All festivals' },
              ...festivals.map((f) => ({ value: f.id, label: f.name })),
            ]}
            value={isAllFestivals ? ALL_FESTIVALS : effectiveFestivalId}
            onChange={(v) => setParam('festival', typeof v === 'string' ? v : '')}
            placeholder="Select festival"
          />
        </div>
        <div className="w-48">
          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            Status
          </label>
          <Select
            options={STATUS_FILTER_OPTIONS}
            value={status}
            onChange={(v) => setParam('status', typeof v === 'string' ? v : '')}
            placeholder="All statuses"
          />
        </div>
        <div className="w-44">
          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            Slot
          </label>
          <Select
            options={SLOT_OPTIONS.map((o) => ({ value: o.value || '', label: o.label }))}
            value={slot}
            onChange={(v) => setParam('slot', typeof v === 'string' ? v : '')}
            placeholder="Any slot"
          />
        </div>
        <div className="w-52">
          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            Location
          </label>
          <Select
            options={[
              { value: '', label: 'All locations' },
              ...locations.map((l) => ({ value: l.id, label: l.name })),
            ]}
            value={effectiveLocationId}
            onChange={(v) => setParam('location', typeof v === 'string' ? v : '')}
            placeholder="All locations"
          />
        </div>
        <div className="w-64">
          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            Delivery window
          </label>
          <DatePicker
            value={from ? new Date(from + 'T00:00:00') : undefined}
            onChange={(d) => setSingleDate(d as Date | undefined)}
            placeholder="Any date"
          />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            Search
          </label>
          <div className="flex items-stretch gap-2">
            <div className="flex-1">
              <Input
                ref={searchInputRef}
                placeholder="Booking # · name · phone"
                value={searchInput}
                onChange={(v) => handleSearch(v)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchNow(); }}
              />
            </div>
            <Button variant="primary" onClick={searchNow} aria-label="Search bookings">
              <SearchIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            setSearchParams(() => {
              const next = new URLSearchParams();
              if (effectiveFestivalId) next.set('festival', effectiveFestivalId);
              return next;
            });
            setSearchInput('');
          }}
        >
          Reset
        </Button>
      </div>

      {/* Results */}
      {bookings.length === 0 && !isLoading ? (
        <div className={cn(GLASS, 'p-12 animate-fade-in')}>
          <EmptyState
            variant="inline"
            icon={<CalendarIcon className="h-full w-full" />}
            title="No bookings match filters"
            description="Try clearing filters, picking a different festival, or create a booking to get started."
            action={
              isManager && effectiveFestivalId
                ? { label: 'New Booking', onClick: () => setNewBookingOpen(true) }
                : { label: 'Open Calendar', onClick: () => navigate(`/bookings/calendar?festival=${effectiveFestivalId}`) }
            }
          />
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className={cn(GLASS, 'hidden md:block animate-slide-up')}>
            <DataTable<BookingListItem>
              columns={columns}
              data={bookings}
              loading={isLoading}
              totalCount={total}
              currentPage={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              stickyHeader
              emptyState={{
                title: 'No bookings match filters',
                description: 'Adjust filters or switch to calendar view',
                icon: <ListIcon />,
              }}
            />
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3 animate-fade-in">
            {bookings.map((b) => (
              <BookingMobileCard key={b.id} booking={b} onOpen={(id) => navigate(`/bookings/${id}`)} />
            ))}
          </div>
        </>
      )}

      {activeFestival && (
        <NewBookingModal
          open={newBookingOpen}
          onClose={() => setNewBookingOpen(false)}
          festivalId={activeFestival.id}
          festivalName={activeFestival.name}
          advancePercentage={activeFestival.advance_percentage ?? 50}
          locations={locations.map((l) => ({ id: l.id, name: l.name }))}
          defaultLocationId={effectiveLocationId || undefined}
          onCreated={() => setNewBookingOpen(false)}
        />
      )}
    </>
  );
}

export function BookingsListPage() {
  return (
    <PageContainer
      title="Customer Bookings"
      breadcrumbs={[{ label: 'Bookings' }]}
    >
      <ErrorBoundary>
        <BookingsListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
